// api_client.js

import { post, get, put, del, ApiError } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * API Client for Pylo API
 * Centralizes all API calls and provides consistent error handling
 */
class PyloApiClient {
  /**
   * Initialize the API client
   * @param {Object} config - Configuration options
   * @param {string} config.apiName - API name for Amplify
   * @param {string} config.basePath - Base path for API endpoints
   */
  constructor(config = {}) {
    this.apiName = config.apiName || 'pylo';
    this.basePath = config.basePath || '/pylo';
    this.idToken = null;
    this.accessToken = null;
  }

  /**
   * Set the access token for all subsequent requests
   * @param {string} idToken - ID token
   * @param {string} accessToken - Access token
   */
  setAuthTokens(idToken, accessToken) {
    this.idToken = idToken;
    this.accessToken = accessToken;
  }

  /**
   * Get authentication headers
   * @returns {Object} Headers object with authentication
   */
  getAuthHeaders() {
    if (!this.idToken) {
      throw new Error('ID token not set. Call setAuthTokens() first.');
    } else if (!this.accessToken) {
      throw new Error('Access token not set. Call setAuthTokens() first.');
    }
    return {
      'Authorization': `Bearer ${this.idToken}`,
      'X-Access-Token': `Bearer ${this.accessToken}`
    };
  }

  /**
   * Handle API response
   * @param {Response} response - API response
   * @returns {Promise<Object>} Parsed response data
   * @throws {Error} API error
   */
  async handleResponse(restOp) {
    try {
      const httpResponse = await restOp.response;
      const data = await httpResponse.body.json();
      
      if (httpResponse.statusCode !== 200) {
        const errorMessage = data.message || 'Unknown error occurred';
        throw new ApiError({ statusCode: httpResponse.statusCode, body: JSON.stringify({ message: errorMessage }) });
      }
      
      return data;
    } catch (error) {
      // If it's already an ApiError, just rethrow it
      if (error instanceof ApiError) {
        throw error;
      }
      
      // For other errors, create a more informative error
      console.log('Error in handleResponse:', error);
      throw new Error(`Error processing response: ${error.message}`);
    }
  }

  /**
   * Make API request
   * @param {string} method - HTTP method
   * @param {string} path - API endpoint path
   * @param {Object} options - Request options
   * @param {boolean} requiresAuth - Whether this request requires authentication
   * @returns {Promise<Object>} Response data
   */
  async request(method, path, options = {}, requiresAuth = true) {

    const fullPath = `${this.basePath}${path}`;
      
    let requestFn;
    switch (method.toLowerCase()) {
      case 'get': requestFn = get; break;
      case 'post': requestFn = post; break;
      case 'put': requestFn = put; break;
      case 'delete': requestFn = del; break;
      default: requestFn = null;
    }
      
    try {
      if (!requestFn) {
        throw new Error(`Unsupported method: ${method}`);
      }
      // Apply authentication headers if required
      if (requiresAuth) {
        options.headers = {
          ...options.headers,
          ...this.getAuthHeaders()
        };
      }
      
      const response = requestFn({
        apiName: this.apiName,
        path: fullPath,
        options: options
      });
      
      const result = await this.handleResponse(response)
      return result
    } catch (error) {
      console.log(`API ${method} request failed:`, error);
      
      // Check if it's an ApiError with a response property
      if (error.response) {
        try {
          // Try to access the response data
          const errorResponse = await error.response;
          const { statusCode, headers, errorBody} = errorResponse;
          
          // Handle 401 unauthorized errors
          if (statusCode === 401) {
            try {
              console.log("Unauthorized, refreshing and trying again")
              const { tokens, credentials, identityId, userSub } =
                await fetchAuthSession();
              const { idToken, accessToken } = tokens;
              this.setAuthTokens(idToken, accessToken);
              options.headers.Authorization = `Bearer ${this.idToken}`
              options.headers['X-Access-Token'] = `Bearer ${this.accessToken}`
              const resp = requestFn({
                apiName: this.apiName,
                path: fullPath,
                options: options
              });
              return await this.handleResponse(resp)
            } catch (e) {
              // Failed again so just throw
              console.log(`API ${method} request failed again:`, error);
              throw e
            }
          }
          // Rethrow with more context
          throw new ApiError({ 
            statusCode: statusCode, 
            body: JSON.stringify(errorBody) 
          });
        } catch (parseError) {
          // If we can't parse the error response, just throw the original error
          throw error;
        }
      } else {
        // For other types of errors, just throw them as is
        throw error;
      }
    }
  }

  /**
   * GET request
   * @param {string} path - API endpoint path
   * @param {Object} options - Request options
   * @param {boolean} requiresAuth - Whether this request requires authentication
   * @returns {Promise<Object>} Response data
   */
  get(path, options = {}, requiresAuth = true) {
    return this.request('get', path, options, requiresAuth);
  }

  /**
   * POST request
   * @param {string} path - API endpoint path
   * @param {Object} body - Request body
   * @param {Object} options - Additional request options
   * @param {boolean} requiresAuth - Whether this request requires authentication
   * @returns {Promise<Object>} Response data
   */
  post(path, body, options = {}, requiresAuth = true) {
    return this.request('post', path, {
      ...options,
      body
    }, requiresAuth);
  }

  /**
   * PUT request
   * @param {string} path - API endpoint path
   * @param {Object} body - Request body
   * @param {Object} options - Additional request options
   * @param {boolean} requiresAuth - Whether this request requires authentication
   * @returns {Promise<Object>} Response data
   */
  put(path, body, options = {}, requiresAuth = true) {
    return this.request('put', path, {
      ...options,
      body
    }, requiresAuth);
  }

  /**
   * DELETE request
   * @param {string} path - API endpoint path
   * @param {Object} options - Request options
   * @param {boolean} requiresAuth - Whether this request requires authentication
   * @returns {Promise<Object>} Response data
   */
  delete(path, options = {}, requiresAuth = true) {
    return this.request('delete', path, options, requiresAuth);
  }

  // Domain-specific API methods

  /**
   * Get devices
   * @param {string} accountId - Account ID
   * @returns {Promise<Array>} List of devices
   */
  getDevices(accountId) {
    return this.post('/devices', {
      action: 'get_devices',
      account_id: accountId
    }, {}, true);
  }

  /**
   * Get orders
   * @param {string} accountId - Account ID
   * @returns {Promise<Array>} List of orders
   */
  getOrders(accountId) {
    return this.get('/orders', {
      queryParams: {
        account_id: accountId
      }
    }, true);
  }

  /**
   * Get account
   * @returns {Promise<Object>} Account data
   */
  getAccount() {
    return this.post('/account', {}, {}, true);
  }

  /**
   * Get webhook delivery logs
   * @param {string} accountId - Account ID (device_customer_id)
   * @param {string} eventType - Optional filter: 'DEVICE_MEASUREMENT' or 'ORDER_UPDATES'
   * @returns {Promise<Object>} Webhook delivery logs
   */
  getWebhookDeliveries(accountId, eventType = null) {
    const body = {
      action: 'get_deliveries',
      device_customer_id: accountId,
    };
    if (eventType) {
      body.event_type = eventType;
    }
    return this.post('/webhooks', body, {}, true);
  }
}

const apiClient = new PyloApiClient();
export default apiClient;

export { PyloApiClient };